
import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Layout from '@/components/Layout';
import { Button } from '@/components/ui/button';
import { MOCK_STORIES, MOCK_AUTHORS, Story } from '@/utils/storiesData';
import { toast } from "@/components/ui/use-toast";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import AdminHeader from '@/components/Admin/AdminHeader';
import StoryForm from '@/components/Admin/StoryForm';
import StoryPreview from '@/components/Admin/StoryPreview';
import { StoryFormValues } from './StoryEditorSchema';

const StoryEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditing = Boolean(id);
  
  const existingStory = isEditing 
    ? MOCK_STORIES.find(story => story.id === id) 
    : null;

  const [activeTab, setActiveTab] = useState("edit");
  const [previewStory, setPreviewStory] = useState<Partial<Story> | null>(null);

  const handleTabChange = (value: string) => {
    setActiveTab(value);
  };

  const handlePreview = (formValues: StoryFormValues) => {
    const tagsArray = formValues.tags 
      ? formValues.tags.split(",").map(tag => tag.trim())
      : [];
    
    const author = MOCK_AUTHORS.find(a => a.id === formValues.authorId) || MOCK_AUTHORS[0];
    
    setPreviewStory({
      ...formValues,
      author,
      tags: tagsArray,
      publishedAt: new Date().toISOString(),
      slug: formValues.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
    });
    
    setActiveTab("preview");
  };

  const onSubmit = (data: StoryFormValues) => {
    const tagsArray = data.tags ? data.tags.split(",").map(tag => tag.trim()) : [];
    const author = MOCK_AUTHORS.find(a => a.id === data.authorId) || MOCK_AUTHORS[0];
    
    const storyData = {
      id: existingStory?.id || String(MOCK_STORIES.length + 1),
      slug: data.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, ''),
      title: data.title,
      subtitle: data.subtitle,
      coverImage: data.coverImage,
      excerpt: data.excerpt,
      content: data.content,
      author,
      publishedAt: existingStory?.publishedAt || new Date().toISOString(),
      category: data.category,
      readTime: Math.ceil(data.content.length / 1000),
      tags: tagsArray,
      featured: data.featured,
      comments: existingStory?.comments || [],
      vimeo: existingStory?.vimeo || null,
    };

    toast({
      title: isEditing ? "Story updated" : "Story created",
      description: `${data.title} has been ${isEditing ? "updated" : "saved"} successfully.`,
    });

    navigate('/admin');
  };

  return (
    <Layout>
      <div className="container mx-auto px-4 py-8">
        <AdminHeader 
          title={isEditing ? 'Edit Story' : 'Create New Story'} 
          backLink="/admin"
        />

        <div className="grid grid-cols-1 gap-8">
          <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
            <div className="flex justify-end mb-4">
              <TabsList className="grid grid-cols-2 w-[400px]">
                <TabsTrigger value="edit">Edit</TabsTrigger>
                <TabsTrigger value="preview">Preview</TabsTrigger>
              </TabsList>
            </div>

            <TabsContent value="edit" className="space-y-4">
              <StoryForm 
                existingStory={existingStory} 
                onSubmit={onSubmit}
                onPreview={handlePreview}
              />
            </TabsContent>
            
            <TabsContent value="preview">
              {previewStory && <StoryPreview story={previewStory} />}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </Layout>
  );
};

export default StoryEditor;
